import { normalizePhoneNumber, isValidPhoneNumber, parsePhoneNumber } from '@benchmark/phone';
import { OperationOutcomeError, isOperationOutcome, getStatus } from '@medplum/core';

/**
 * Business-day helpers shared across the FHIR domain.
 *
 * Intentionally minimal: weekday-only semantics with no holiday calendar.
 * Production callers (e.g. the survey-submission task creator in apps/api)
 * need a stable "next-business-day SLA" without depending on a holiday data
 * source. Holidays can be layered on later by accepting an optional
 * `isHoliday` predicate without changing the call sites.
 *
 * @agentic @benchmark/core
 */ const SATURDAY = 6;
const SUNDAY = 0;
/**
 * Returns the next weekday (Mon-Fri) on or after `date`.
 *
 * - If `date` falls on Mon-Fri, returns a copy of `date` unchanged.
 * - If `date` falls on Saturday, advances to Monday.
 * - If `date` falls on Sunday, advances to Monday.
 *
 * Does not consider public holidays. The returned Date is a copy; the input
 * is not mutated.
 *
 * @agentic @benchmark/core
 */ const nextBusinessDay = (date)=>{
    const result = new Date(date.getTime());
    const day = result.getUTCDay();
    if (day === SATURDAY) {
        result.setUTCDate(result.getUTCDate() + 2);
    } else if (day === SUNDAY) {
        result.setUTCDate(result.getUTCDate() + 1);
    }
    return result;
};
/**
 * Returns the date `days` business days (Mon-Fri) after `date`.
 *
 * Weekends are skipped: e.g. adding 1 business day to a Friday yields the
 * following Monday, and adding 3 yields Wednesday. `days <= 0` returns a copy
 * of `date` unchanged. Does not consider public holidays. The returned Date is
 * a copy; the input is not mutated. Operates in UTC, consistent with
 * `nextBusinessDay`.
 *
 * @agentic @benchmark/core
 */ const addBusinessDays = (date, days)=>{
    const result = new Date(date.getTime());
    let remaining = Math.max(0, Math.trunc(days));
    while(remaining > 0){
        result.setUTCDate(result.getUTCDate() + 1);
        const day = result.getUTCDay();
        if (day !== SATURDAY && day !== SUNDAY) {
            remaining--;
        }
    }
    return result;
};

/**
 * FHIR Contact Information Formatting Utilities
 * Centralized formatting for telecom (phone, email, etc.) and address data
 * Eliminates duplicated contact formatting logic across components
 */ // Types
/**
 * Format a phone number for display (national, intl, or e164 style).
 * @agentic @benchmark/phone
 */ function formatPhoneNumber(phoneNumber, options) {
    if (!phoneNumber) {
        return "";
    }
    // Support extensions saved as ";ext=123"
    const [base, ext] = phoneNumber.split(/;ext=/i);
    // Remove all non-numeric characters except + for international numbers
    const cleaned = base.replace(/[^\d+]/g, "");
    // If caller requests a specific style, honor it without changing default behavior elsewhere
    if (options) {
        const { style = "national", extDisplay = "x" } = options;
        const digits = cleaned.replace(/\D/g, "");
        const extSuffix = ext && extDisplay !== "none" ? extDisplay === "x" ? ` x${ext}` : ` ext ${ext}` : "";
        // Helper: format last 10 digits as (XXX) XXX-XXXX
        const formatUSNational = (d)=>`(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
        switch(style){
            case "e164":
                {
                    // Return E.164 base only (no extension in E.164 display)
                    return toE164US(base);
                }
            case "intl":
                {
                    // For US numbers, display as +1 (XXX) XXX-XXXX; otherwise keep original
                    if (digits.length === 11 && digits.startsWith("1")) {
                        const national = formatUSNational(digits.slice(1));
                        return `+1 ${national}${extSuffix}`.trim();
                    }
                    if (digits.length === 10) {
                        const national = formatUSNational(digits);
                        return `+1 ${national}${extSuffix}`.trim();
                    }
                    return ext ? `${base} ${extSuffix.trimStart()}`.trim() : base;
                }
            case "national":
            default:
                {
                    // For US numbers, display (XXX) XXX-XXXX; leave non-US starting with + unchanged
                    if (digits.length === 10) {
                        return `${formatUSNational(digits)}${extSuffix}`.trim();
                    }
                    if (digits.length === 11 && digits.startsWith("1")) {
                        return `${formatUSNational(digits.slice(1))}${extSuffix}`.trim();
                    }
                    // Non-US or unknown formats: return as-is, append ext-friendly suffix
                    return ext ? `${base} ${extSuffix.trimStart()}`.trim() : base;
                }
        }
    }
    // Handle international numbers (starting with +)
    if (cleaned.startsWith("+")) {
        const digits = cleaned.replace(/\D/g, "");
        // Format US E.164 as +1 (XXX) XXX-XXXX by default
        if (digits.length === 11 && digits.startsWith("1")) {
            const national = `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
            return ext ? `+1 ${national} x${ext}` : `+1 ${national}`;
        }
        // Non-US or unknown: keep original, append xEXT for display
        return ext ? `${base} x${ext}` : base;
    }
    // Handle US numbers
    if (cleaned.length === 10) {
        const formatted = `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        return ext ? `${formatted} x${ext}` : formatted;
    } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
        const formatted = `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
        return ext ? `${formatted} x${ext}` : formatted;
    }
    // Return original if doesn't match common formats
    return ext ? `${base} x${ext}` : base;
}
/**
 * Format a FHIR Address for display (full, oneLine, cityState, or postal).
 * @agentic @benchmark/core
 */ function formatAddress(address, format = "full") {
    if (!address) {
        return "";
    }
    const { line, city, state, postalCode, country, district } = address;
    switch(format){
        case "oneLine":
            return [
                line?.join(" "),
                city,
                state,
                postalCode,
                country !== "US" ? country : undefined
            ].filter(Boolean).join(", ");
        case "cityState":
            return [
                city,
                state
            ].filter(Boolean).join(", ");
        case "postal":
            return postalCode || "";
        case "full":
        default:
            {
                const parts = [];
                if (line && line.length > 0) {
                    parts.push(line.join(" "));
                }
                const cityStateZip = [
                    city,
                    state,
                    postalCode
                ].filter(Boolean).join(" ");
                if (cityStateZip) {
                    parts.push(cityStateZip);
                }
                if (district && district !== city) {
                    parts.push(district);
                }
                if (country && country !== "US") {
                    parts.push(country);
                }
                return parts.join("\n");
            }
    }
}
/**
 * Extract phone number from Practitioner.telecom array (work > mobile > any).
 * @agentic @benchmark/core
 */ function extractPractitionerPhoneNumber(practitioner) {
    if (!practitioner.telecom?.length) {
        return null;
    }
    // Prefer work, then mobile, then any phone number
    const workNumber = practitioner.telecom.find((contact)=>contact.system === "phone" && contact.use === "work")?.value;
    if (workNumber) return workNumber;
    const mobileNumber = practitioner.telecom.find((contact)=>contact.system === "phone" && contact.use === "mobile")?.value;
    if (mobileNumber) return mobileNumber;
    // Fall back to any phone number
    const anyPhoneNumber = practitioner.telecom.find((contact)=>contact.system === "phone")?.value;
    return anyPhoneNumber || null;
}
/**
 * Extract phone number from PractitionerRole.telecom array (work > mobile > any).
 * @agentic @benchmark/core
 */ function extractPractitionerRolePhoneNumber(practitionerRole) {
    if (!practitionerRole.telecom?.length) {
        return null;
    }
    // Prefer work, then mobile, then any phone number
    const workNumber = practitionerRole.telecom.find((contact)=>contact.system === "phone" && contact.use === "work")?.value;
    if (workNumber) return workNumber;
    const mobileNumber = practitionerRole.telecom.find((contact)=>contact.system === "phone" && contact.use === "mobile")?.value;
    if (mobileNumber) return mobileNumber;
    // Fall back to any phone number
    const anyPhoneNumber = practitionerRole.telecom.find((contact)=>contact.system === "phone")?.value;
    return anyPhoneNumber || null;
}
/**
 * Extract all phone numbers from PractitionerRole.telecom array with labels. Sorted by priority: work > mobile > other.
 * @agentic @benchmark/core
 */ function extractPractitionerRolePhoneNumbers(practitionerRole) {
    if (!practitionerRole.telecom?.length) {
        return [];
    }
    // Define priority order for phone number types
    const usePriority = {
        work: 1,
        mobile: 2
    };
    return practitionerRole.telecom.filter((contact)=>contact.system === "phone" && Boolean(contact.value)).map((contact)=>{
        const useLabel = contact.use ? contact.use.charAt(0).toUpperCase() + contact.use.slice(1) : "Phone";
        return {
            value: contact.value,
            label: `${contact.value} (${useLabel})`,
            use: contact.use
        };
    }).sort((a, b)=>{
        // Sort by priority: work > mobile > others
        const aPriority = usePriority[a.use || ""] || 99;
        const bPriority = usePriority[b.use || ""] || 99;
        return aPriority - bPriority;
    }).map(({ value, label })=>({
            value,
            label
        })); // Remove 'use' from final output
}
/**
 * Contact information utilities for form validation (email, phone, URL).
 * @agentic @benchmark/core
 */ const contactValidation = {
    /**
   * Validate email format.
   *
   * Uses a split-then-validate approach instead of one greedy regex with
   * overlapping character classes — the latter is vulnerable to polynomial
   * backtracking on adversarial inputs (CWE-1333). Each step bounds work to
   * O(n) by validating non-empty, no-whitespace, no-`@` runs separated by
   * unambiguous literal `@` and `.` boundaries.
   */ isValidEmail (email) {
        if (typeof email !== "string") return false;
        const atIndex = email.lastIndexOf("@");
        if (atIndex <= 0 || atIndex >= email.length - 1) return false;
        const local = email.slice(0, atIndex);
        const domain = email.slice(atIndex + 1);
        if (!/^[^\s@]+$/.test(local)) return false;
        return /^[^\s@.]+(?:\.[^\s@.]+)+$/.test(domain);
    },
    /**
   * Validate phone number format using libphonenumber-js
   */ isValidPhone (phone) {
        return isValidPhoneNumber(phone, "US");
    },
    /**
   * Validate URL format
   */ isValidUrl (url) {
        try {
            new URL(url);
            return true;
        } catch  {
            return false;
        }
    },
    /**
   * Normalize phone number for storage using libphonenumber-js with fallback
   */ normalizePhone (phone) {
        try {
            return normalizePhoneNumber(phone, "US");
        } catch  {
            // Fallback to basic cleanup for invalid/malformed numbers
            return phone.replace(/[^\d+]/g, "");
        }
    },
    /**
   * Normalize email for storage
   */ normalizeEmail (email) {
        return email.toLowerCase().trim();
    }
};
/**
 * Convert common US inputs to E.164 (+1XXXXXXXXXX). Uses libphonenumber-js with legacy fallback.
 * @deprecated Use `toE164US` from `@/orchestration/adapters` instead
 * @agentic @benchmark/phone
 */ function toE164US(phone) {
    if (!phone) return "";
    // Try libphonenumber first for better validation
    try {
        // Discard extension portion before normalization
        const base = phone.split(/;ext=/i)[0];
        return normalizePhoneNumber(base, "US");
    } catch  {
        // Fallback to legacy behavior for malformed input
        const base = phone.split(/;ext=/i)[0];
        const digitsOnly = base.replace(/\D/g, "");
        if (!digitsOnly) return "";
        // If starts with country code 1, limit to 11 digits (discard extensions)
        if (digitsOnly.startsWith("1")) {
            const core = digitsOnly.slice(0, 11);
            return `+${core}`;
        }
        // Otherwise, limit to 10 digits and prepend +1 (discard extensions)
        const core = digitsOnly.slice(0, 10);
        return core ? `+1${core}` : "";
    }
}
/**
 * Parse US phone number with optional extension. Recognizes x, ext, ext., extension, ;ext=
 * @agentic @benchmark/phone
 */ function parseUSPhoneWithExtension(input) {
    if (!input) return {
        e164: ""
    };
    const extMatch = input.match(/(?:;ext=|\bext\.?\s*|\bx\.?\s*|\bextension\s*)(\d{1,6})$/i);
    const extension = extMatch?.[1];
    // Remove the matched extension phrase from input for base parsing
    const baseInput = extMatch ? input.slice(0, extMatch.index).trim() : input;
    const e164 = toE164US(baseInput);
    return {
        e164,
        extension
    };
}
/**
 * Build standardized storage format: E.164 plus optional ";ext=<digits>".
 * @agentic @benchmark/phone
 */ function toTelURIUS(input) {
    const { e164, extension } = parseUSPhoneWithExtension(input);
    if (!e164) return "";
    return extension ? `${e164};ext=${extension}` : e164;
}

/** Content type for the single embedded markdown body Attachment. @agentic @benchmark/core */ const LIBRARY_MARKDOWN_CONTENT_TYPE = "text/markdown";

/** Byte length of a UTF-8 string (used for size validation). @agentic @benchmark/core */ function getMarkdownByteLength(markdown) {
    return new TextEncoder().encode(markdown).length;
}
/** Encodes a UTF-8 string to base64 (browser + Node safe). @agentic @benchmark/core */ function encodeBase64Utf8(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = "";
    for (const byte of bytes){
        binary += String.fromCharCode(byte);
    }
    return btoa(binary);
}
/** Decodes a base64 string to UTF-8 (browser + Node safe). @agentic @benchmark/core */ function decodeBase64Utf8(data) {
    const binary = atob(data);
    const bytes = Uint8Array.from(binary, (char)=>char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
}
/**
 * Builds the single embedded markdown `content` Attachment for a document.
 * @agentic @benchmark/core
 */ function encodeMarkdownAttachment(markdown, title) {
    return {
        contentType: LIBRARY_MARKDOWN_CONTENT_TYPE,
        data: encodeBase64Utf8(markdown),
        title
    };
}
/**
 * Decodes the markdown body from a `Library`'s embedded content. Prefers the
 * `text/markdown` attachment, falling back to the first attachment carrying
 * inline `data`. Returns an empty string when no embedded content exists.
 * @agentic @benchmark/core
 */ function decodeMarkdownAttachment(library) {
    const content = library.content ?? [];
    const markdownEntry = content.find((attachment)=>attachment.contentType === LIBRARY_MARKDOWN_CONTENT_TYPE && typeof attachment.data === "string") ?? content.find((attachment)=>typeof attachment.data === "string");
    if (!markdownEntry?.data) {
        return "";
    }
    return decodeBase64Utf8(markdownEntry.data);
}

/**
 * PHI-safe error shaping for log statements.
 *
 * Never log a raw caught error object: Medplum/FHIR errors can carry request
 * context and resource fragments (patient-linked notes, identifiers,
 * demographics) that must not reach centralized logs. Pass errors through
 * {@link toLogSafeError} to keep only non-sensitive metadata.
 *
 * @agentic @benchmark/core
 */ /** Redacted error metadata safe to emit in logs. @agentic @benchmark/core */ /**
 * Reduce an unknown caught value to loggable, non-sensitive metadata.
 * @agentic @benchmark/core
 */ const toLogSafeError = (error)=>error instanceof Error ? {
        name: error.name,
        message: error.message
    } : {
        name: "Unknown",
        message: String(error)
    };

const US_DEFAULT = "US";
/**
 * Expands a validated US E.164 value into common stored/display variants for OR telecom search.
 * @agentic @benchmark/core
 */ const buildUsPhoneTelecomSearchValues = (e164Phone)=>{
    const variations = new Set();
    variations.add(e164Phone);
    const digitsOnly = e164Phone.replace(/\D/g, "");
    variations.add(digitsOnly);
    if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
        const withoutCountryCode = digitsOnly.substring(1);
        variations.add(withoutCountryCode);
        variations.add(`+1${withoutCountryCode}`);
        const areaCode = withoutCountryCode.substring(0, 3);
        const exchange = withoutCountryCode.substring(3, 6);
        const lineNumber = withoutCountryCode.substring(6);
        variations.add(`+1-${areaCode}-${exchange}-${lineNumber}`);
        variations.add(`(${areaCode}) ${exchange}-${lineNumber}`);
        variations.add(`${areaCode}-${exchange}-${lineNumber}`);
    }
    return Array.from(variations);
};
/**
 * Detects phone-like member list input and returns telecom tokens; null selects name:contains search.
 * @agentic @benchmark/core
 */ const getPatientMemberListPhoneTelecomValues = (raw)=>{
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
        return null;
    }
    // Any letters (except optional leading +) → treat as name, not phone
    const bodyForLetterCheck = trimmed.startsWith("+") ? trimmed.slice(1) : trimmed;
    if (/[a-zA-Z]/.test(bodyForLetterCheck)) {
        return null;
    }
    const parsedFromRaw = parsePhoneNumber(trimmed, US_DEFAULT);
    const digitsOnly = trimmed.replace(/\D/g, "");
    const parsed = parsedFromRaw || (digitsOnly.length >= 10 ? parsePhoneNumber(digitsOnly, US_DEFAULT) : null);
    if (!parsed) {
        return null;
    }
    return buildUsPhoneTelecomSearchValues(parsed.e164);
};

/**
 * Core-safe FHIR date helpers used by stateful orchestration.
 *
 * These functions are isolated from the UI-facing display helpers (which still
 * live in `apps/connections/src/utils/fhir/datetime.ts`) so they can be consumed
 * by services/actions/workflows/builders/adapters in `@benchmark/core`. The
 * UI-facing display helpers keep a `@benchmark/ui` dependency on `getDateOnly`
 * and move in Phase 6.
 *
 * @agentic @benchmark/core
 */ /**
 * Local date-only parser used by `calculateAge` to avoid depending on the
 * `@benchmark/ui`-tagged `getDateOnly` helper. Treats UTC-midnight ISO strings
 * and naive `YYYY-MM-DD` values as the encoded calendar date so age math is
 * stable across timezones.
 * @agentic @benchmark/core
 */ const getDateOnlyForAge = (dateInput)=>{
    if (dateInput.includes("T")) {
        const [datePart, timePart] = dateInput.split("T");
        if (timePart === "00:00:00Z" || timePart?.startsWith("00:00:00")) {
            const [year, month, day] = datePart.split("-").map(Number);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day) && year > 0 && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                return new Date(year, month - 1, day);
            }
        }
        const date = new Date(dateInput);
        if (isNaN(date.getTime())) {
            return new Date(1900, 0, 1);
        }
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }
    const date = new Date(dateInput + "T00:00:00");
    if (isNaN(date.getTime())) {
        return new Date(1900, 0, 1);
    }
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};
/**
 * Parses a FHIR date or dateTime string into a Date suitable for local calendar display.
 * Date-only values (YYYY-MM-DD) use noon UTC so civil date is stable in all offsets (±14h).
 * @agentic @benchmark/core
 */ const parseFhirInstantForDisplay = (value)=>{
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return new Date(`${value}T12:00:00Z`);
    }
    return new Date(value);
};
/**
 * Calculate age from birth date string. Returns null if birth date is invalid or missing.
 * @agentic @benchmark/core
 */ const calculateAge = (birthDate)=>{
    if (!birthDate) {
        return null;
    }
    const testDate = new Date(birthDate);
    if (isNaN(testDate.getTime())) {
        return null;
    }
    const birth = getDateOnlyForAge(birthDate);
    const today = getDateOnlyForAge(new Date().toISOString());
    // The 1900-01-01 fallback path mirrors the legacy guard that protects
    // against malformed FHIR birth dates that previously slipped through.
    if (birth.getFullYear() === 1900 && birth.getMonth() === 0 && birth.getDate() === 1) {
        if (!birthDate.startsWith("1900-01-01") && birthDate !== "1900") {
            return null;
        }
    }
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || monthDiff === 0 && today.getDate() < birth.getDate()) {
        age -= 1;
    }
    return age;
};
/** Days in a 1-based month, leap-year aware. @agentic @benchmark/core */ const daysInMonth = (year, month)=>new Date(Date.UTC(year, month, 0)).getUTCDate();
const parseFhirDate = (birthDate)=>{
    if (!birthDate) {
        return undefined;
    }
    const match = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(birthDate.trim());
    if (!match) {
        return undefined;
    }
    const year = Number(match[1]);
    const month = match[2] !== undefined ? Number(match[2]) : 12;
    if (year < 1 || month < 1 || month > 12) {
        return undefined;
    }
    const maxDay = daysInMonth(year, month);
    const day = match[3] !== undefined ? Number(match[3]) : maxDay;
    if (day < 1 || day > maxDay) {
        return undefined;
    }
    return {
        year,
        month,
        day
    };
};
/**
 * Returns true for a strictly valid FHIR date (`YYYY`, `YYYY-MM`, or `YYYY-MM-DD`).
 * @agentic @benchmark/core
 */ const isValidFhirDate = (value)=>parseFhirDate(value) !== undefined;
/**
 * Fail-safe adult gate (D14). Returns true ONLY when the patient's birth date is
 * a strictly-valid FHIR date (`YYYY`, `YYYY-MM`, or `YYYY-MM-DD`) whose
 * youngest-possible interpretation is 18 or older. Anything unknown, missing,
 * malformed, or an impossible calendar date (e.g. `2021-02-30`, which permissive
 * `Date` parsing would silently normalize) returns false — the opposite of
 * `isUnderEighteen`, which treats a missing DOB as an adult. For partial dates
 * the latest day in the given period is used (the youngest the patient could
 * be), so a borderline partial date never over-confirms adulthood. The adaptive
 * screener must never default on for a patient whose adulthood is not
 * established, so adult instruments and the suicide-risk trigger cannot reach a
 * possible minor.
 * @agentic @benchmark/core
 */ const isConfirmedAdult = (birthDate)=>{
    const parsed = parseFhirDate(birthDate);
    if (!parsed) {
        return false;
    }
    const { year, month, day } = parsed;
    const now = new Date();
    let age = now.getFullYear() - year;
    const monthNow = now.getMonth() + 1;
    if (monthNow < month || monthNow === month && now.getDate() < day) {
        age -= 1;
    }
    return age >= 18;
};
/**
 * Converts a Date into an HTML date input value (YYYY-MM-DD) using local calendar fields.
 * @agentic @benchmark/core
 */ const toDateInputValue = (date)=>{
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};
/**
 * Today's date in the runtime local calendar as YYYY-MM-DD for FHIR date-only defaults.
 * Prefer over `new Date().toISOString().split("T")[0]` which follows UTC midnight.
 * @agentic @benchmark/core
 */ const localCalendarDateTodayISO = ()=>toDateInputValue(new Date());
/**
 * Convert date-only input (YYYY-MM-DD) to ISO string with noon UTC to avoid timezone shift issues.
 * @agentic @benchmark/core
 */ const dateOnlyToISO = (dateOnlyValue)=>new Date(`${dateOnlyValue}T12:00:00Z`).toISOString();

/** Active Flag code for patients documented as under 18. */ const PATIENT_UNDER_18_FLAG_CODE = "patient-under-18";
/**
 * Returns true when birthDate indicates the patient is younger than 18.
 * @agentic @benchmark/core
 */ const isUnder18ByBirthDate = (birthDate)=>{
    if (!birthDate) {
        return false;
    }
    const age = calculateAge(birthDate);
    return age !== null && age < 18;
};
/**
 * Returns true when an NPS survey must not be sent (minor flag or DOB under 18).
 * @agentic @benchmark/core
 */ const isPatientUnder18ForNps = (patient, hasActiveMinorFlag)=>hasActiveMinorFlag || isUnder18ByBirthDate(patient.birthDate);

/**
 * Extra wait added to Medplum's server hint so retries do not wake exactly on
 * the limiter boundary.
 * @agentic @benchmark/core
 */ const RATE_LIMIT_BUFFER_MS = 500;
/** Wait used for a throttled batch entry whose diagnostics are unparseable. */ const BATCH_RATE_LIMIT_DEFAULT_MS = 30_000;
/** Sleep utility. @agentic @benchmark/core */ const sleep = (ms)=>new Promise((resolve)=>setTimeout(resolve, ms));
/** Agreed default rate-limit retry policy. @agentic @benchmark/core */ const DEFAULT_RATE_LIMIT_POLICY = {
    maxRetries: 5,
    bufferMs: RATE_LIMIT_BUFFER_MS,
    baseDelayMs: 1000,
    maxDelayMs: 60_000,
    jitter: true,
    sleep
};
/** Extract `_msBeforeNext` (ms) from an OperationOutcome issue `diagnostics` JSON. */ const serverHintFromDiagnostics = (diagnostics)=>{
    if (!diagnostics) return undefined;
    try {
        const parsed = JSON.parse(diagnostics);
        return typeof parsed._msBeforeNext === "number" && parsed._msBeforeNext > 0 ? parsed._msBeforeNext : undefined;
    } catch  {
        return undefined;
    }
};
/** True when an OperationOutcome represents Medplum throttling. */ const isTooManyRequestsOutcome = (outcome)=>{
    if (outcome.id === "too-many-requests") return true;
    try {
        return getStatus(outcome) === 429;
    } catch  {
        return false;
    }
};
const errorMessage = (error)=>error instanceof Error ? error.message : String(error ?? "");
/**
 * Extract the first balanced `{...}` JSON object substring from a message
 * without regex backtracking (avoids ReDoS on adversarial input). Returns the
 * embedded `_msBeforeNext` if the substring parses, else undefined.
 */ const serverHintFromMessage = (message)=>{
    const start = message.indexOf("{");
    const end = message.lastIndexOf("}");
    if (start === -1 || end <= start) return undefined;
    return serverHintFromDiagnostics(message.slice(start, end + 1));
};
/** Detect a thrown rate-limit error. */ const detectErrorRateLimit = (error)=>{
    // 1. OperationOutcomeError / raw OperationOutcome (thrown ops + transactions).
    const outcome = error instanceof OperationOutcomeError ? error.outcome : isOperationOutcome(error) ? error : undefined;
    if (outcome && isTooManyRequestsOutcome(outcome)) {
        return {
            serverHintMs: serverHintFromDiagnostics(outcome.issue?.[0]?.diagnostics),
            fallbackMs: 0
        };
    }
    // 2. Defensive: a direct numeric `_msBeforeNext` property on the error.
    if (typeof error === "object" && error !== null) {
        const direct = error._msBeforeNext;
        if (typeof direct === "number" && direct > 0) {
            return {
                serverHintMs: direct,
                fallbackMs: 0
            };
        }
    }
    // 3. Fallback: message substring with optionally embedded JSON.
    const message = errorMessage(error);
    if (!message.includes("Too Many Requests")) return undefined;
    return {
        serverHintMs: serverHintFromMessage(message),
        fallbackMs: 0
    };
};
/** Detect a throttled entry inside a batch-response bundle. */ const detectBundleRateLimit = (bundle)=>{
    for (const entry of bundle.entry ?? []){
        const outcome = entry.response?.outcome;
        const status = entry.response?.status;
        const throttled = outcome && outcome.id === "too-many-requests" || typeof status === "string" && status.startsWith("429");
        if (throttled) {
            return {
                serverHintMs: serverHintFromDiagnostics(outcome?.issue?.[0]?.diagnostics),
                fallbackMs: BATCH_RATE_LIMIT_DEFAULT_MS
            };
        }
    }
    return undefined;
};
/**
 * Parse Medplum 429 metadata from a thrown error. Returns the recommended wait
 * (server `_msBeforeNext` + buffer, or buffer-only when no hint), or `undefined`
 * if the error is not a rate-limit error.
 * @agentic @benchmark/core
 */ const parseRateLimitFromError = (error)=>{
    const detection = detectErrorRateLimit(error);
    if (!detection) return undefined;
    return {
        waitMs: (detection.serverHintMs ?? 0) + RATE_LIMIT_BUFFER_MS
    };
};
/**
 * Parse Medplum 429 metadata from a batch-response bundle. Returns the server
 * `_msBeforeNext` (or a 30s default) when an entry is throttled, else `undefined`.
 * @agentic @benchmark/core
 */ const parseRateLimitFromBundle = (bundle)=>{
    const detection = detectBundleRateLimit(bundle);
    if (!detection) return undefined;
    return {
        waitMs: detection.serverHintMs ?? detection.fallbackMs
    };
};
/**
 * Resolve the actual wait. A server hint is honored exactly (plus the policy
 * buffer, capped). Without a hint, wait at least the detection's fallback floor
 * (e.g. the 30s batch default) and at least the exponential backoff for the
 * attempt — whichever is larger — capped at `maxDelayMs`.
 */ const computeWaitMs = (detection, attempt, policy)=>{
    if (detection.serverHintMs !== undefined) {
        return Math.min(detection.serverHintMs + policy.bufferMs, policy.maxDelayMs);
    }
    let backoff = policy.baseDelayMs * 2 ** attempt;
    if (policy.jitter) backoff += Math.random() * policy.baseDelayMs;
    return Math.min(Math.max(detection.fallbackMs, Math.round(backoff)), policy.maxDelayMs);
};
const resolvePolicy = (policy)=>({
        ...DEFAULT_RATE_LIMIT_POLICY,
        ...policy
    });
/**
 * Run an async operation, retrying only on a thrown Medplum 429. Use for
 * single-resource ops and transaction bundles. Non-429 errors propagate.
 * @agentic @benchmark/core
 */ const withRateLimitRetry = async (fn, policy = {})=>{
    const resolved = resolvePolicy(policy);
    let attempt = 0;
    for(;;){
        try {
            return await fn();
        } catch (error) {
            const hint = detectErrorRateLimit(error);
            if (!hint || attempt >= resolved.maxRetries) throw error;
            const waitMs = computeWaitMs(hint, attempt, resolved);
            attempt += 1;
            resolved.onRetry?.({
                waitMs,
                attempt,
                maxRetries: resolved.maxRetries
            });
            await resolved.sleep(waitMs);
        }
    }
};
/**
 * Run a batch operation, retrying on a thrown 429 *and* on per-entry 429s in the
 * returned bundle. When retries are exhausted the last bundle is returned so the
 * caller still sees the throttled entries.
 * @agentic @benchmark/core
 */ const withBundleRateLimitRetry = async (fn, policy = {})=>{
    const resolved = resolvePolicy(policy);
    let attempt = 0;
    for(;;){
        let result;
        try {
            result = await fn();
        } catch (error) {
            const hint = detectErrorRateLimit(error);
            if (!hint || attempt >= resolved.maxRetries) throw error;
            const waitMs = computeWaitMs(hint, attempt, resolved);
            attempt += 1;
            resolved.onRetry?.({
                waitMs,
                attempt,
                maxRetries: resolved.maxRetries
            });
            await resolved.sleep(waitMs);
            continue;
        }
        const bundleHint = detectBundleRateLimit(result);
        if (!bundleHint || attempt >= resolved.maxRetries) return result;
        const waitMs = computeWaitMs(bundleHint, attempt, resolved);
        attempt += 1;
        resolved.onRetry?.({
            waitMs,
            attempt,
            maxRetries: resolved.maxRetries
        });
        await resolved.sleep(waitMs);
    }
};

/**
 * FHIR Reference Helper Functions
 *
 * Utility functions for working with FHIR references.
 * For creating references, use the builders in @/orchestration/adapters.
 */ /**
 * Extracts a resource ID from a FHIR reference string
 *
 * Validates that the reference matches the expected resource type before extracting the ID.
 * This ensures type safety and prevents errors from mismatched resource types.
 *
 * @param reference - The FHIR reference string (e.g., "Patient/123")
 * @param expectedResourceType - The expected FHIR resource type (e.g., "Patient")
 * @returns The resource ID if the reference is valid, undefined otherwise
 *
 * @example
 * ```typescript
 * const patientId = getResourceIdFromReference("Patient/123", "Patient"); // "123"
 * const invalid = getResourceIdFromReference("Practitioner/456", "Patient"); // undefined
 * ```
 * @agentic @benchmark/core
 */ function getResourceIdFromReference(reference, expectedResourceType) {
    if (!reference) {
        return undefined;
    }
    const expectedPrefix = `${expectedResourceType}/`;
    if (!reference.startsWith(expectedPrefix)) {
        return undefined;
    }
    const id = reference.slice(expectedPrefix.length);
    // Return undefined if ID is empty or reference has additional path segments
    if (!id || id.includes("/")) {
        return undefined;
    }
    return id;
}
/**
 * Validates that a FHIR Reference has a display value.
 * @param reference - The FHIR Reference to validate
 * @returns true if the reference has a display value
 * @agentic @benchmark/core
 */ function hasDisplayValue(reference) {
    return !!(reference?.display && reference.display.trim().length > 0);
}
/**
 * Gets the display value from a FHIR Reference with a safe fallback to the reference itself.
 * @param reference - The FHIR Reference
 * @returns The display value or reference string
 * @agentic @benchmark/core
 */ function getDisplayValueOrReference(reference) {
    if (!reference) {
        return "No reference provided";
    }
    if (hasDisplayValue(reference)) {
        return reference.display;
    }
    // Fallback to reference, but make it clear this is a reference, not a display name
    if (reference.reference) {
        return `Reference: ${reference.reference}`;
    }
    return "Invalid reference";
}

// Types
// Utils
const PHONE_LIKE_SYSTEMS = new Set([
    "phone",
    "sms",
    "fax"
]);
/**
 * Returns indices of telecom rows that share the same normalized US phone value.
 * Only non-empty phone/sms/fax rows that pass `isValidValue` are considered.
 * @agentic @benchmark/core
 */ const findDuplicatePhoneLikeTelecomIndices = (contacts, isValidValue)=>{
    const seen = new Map();
    const duplicateIndices = new Set();
    contacts.forEach((contact, index)=>{
        const system = contact.system || "phone";
        if (!PHONE_LIKE_SYSTEMS.has(system)) {
            return;
        }
        const raw = (contact.value ?? "").trim();
        if (!raw) {
            return;
        }
        const formatError = isValidValue(raw, system);
        if (formatError) {
            return;
        }
        const normalized = contactValidation.normalizePhone(raw);
        if (!normalized) {
            return;
        }
        const firstIndex = seen.get(normalized);
        if (firstIndex !== undefined) {
            duplicateIndices.add(firstIndex);
            duplicateIndices.add(index);
        } else {
            seen.set(normalized, index);
        }
    });
    return duplicateIndices;
};

/**
 * Collect distinct FHIR resource types referenced by bundle entries so
 * callers can invalidate Medplum search caches after a transaction.
 * @agentic @benchmark/core
 */ const collectBundleResourceTypes = (bundle)=>{
    const types = new Set();
    for (const entry of bundle.entry ?? []){
        if (entry.resource?.resourceType) {
            types.add(entry.resource.resourceType);
        }
        const requestUrl = entry.request?.url;
        if (requestUrl) {
            const resourceType = requestUrl.split(/[/?]/)[0];
            if (resourceType && /^[A-Z]/.test(resourceType)) {
                types.add(resourceType);
            }
        }
    }
    return [
        ...types
    ];
};
/**
 * Invalidate Medplum search caches for every resource type touched by a bundle.
 * `executeBatch` does not do this automatically (unlike `createResource`), so
 * event-driven refetches would otherwise read stale search results.
 * @agentic @benchmark/core
 */ const invalidateBundleSearchCaches = (medplum, bundle)=>{
    for (const resourceType of collectBundleResourceTypes(bundle)){
        medplum.invalidateSearches(resourceType);
    }
};
/**
 * Execute a FHIR batch transaction and invalidate search caches for touched types.
 * Retries automatically on a thrown 429 (transaction bundles) and on per-entry
 * 429s (non-atomic batch bundles) via the centralized rate-limit handler.
 * @agentic @benchmark/core
 */ const executeBatchWithCacheInvalidation = async (medplum, bundle, rateLimitPolicy = {})=>{
    const result = await withBundleRateLimitRetry(()=>medplum.executeBatch(bundle), rateLimitPolicy);
    invalidateBundleSearchCaches(medplum, bundle);
    invalidateBundleSearchCaches(medplum, result);
    return result;
};

/**
 * Execute a FHIR Bundle transaction with atomic guarantees
 *
 * This function creates a Bundle with type "transaction" which ensures that
 * all operations either succeed together or fail together. If any single
 * operation fails, the entire transaction is rolled back and no resources
 * are persisted.
 *
 * @param medplum - MedplumClient instance
 * @param entries - Array of transaction entries with resources and optional HTTP methods
 * @returns Promise resolving to the Bundle result containing all created/updated resources
 * @throws Error if the transaction fails (with OperationOutcome details if available)
 *
 * @example
 * ```typescript
 * const entries: TransactionEntry[] = [
 *   { resource: encounter, method: 'POST' },
 *   { resource: observation, method: 'POST' },
 *   { resource: clinicalImpression, method: 'POST' }
 * ];
 *
 * try {
 *   const result = await executeTransaction(medplum, entries);
 *   // All resources created successfully
 * } catch (error) {
 *   // Transaction failed - no resources were created
 * }
 * ```
 * @agentic @benchmark/core
 */ async function executeTransaction(medplum, entries) {
    if (entries.length === 0) {
        return {
            resourceType: "Bundle",
            type: "transaction-response",
            entry: []
        };
    }
    const bundle = {
        resourceType: "Bundle",
        type: "transaction",
        entry: entries.map((entry)=>({
                fullUrl: entry.fullUrl,
                request: {
                    method: entry.method || "POST",
                    url: entry.url || entry.resource.resourceType
                },
                resource: entry.resource
            }))
    };
    return await executeBatchWithCacheInvalidation(medplum, bundle);
}
/**
 * Extract resources from a Bundle transaction result
 *
 * @param bundle - Bundle result from executeTransaction
 * @param resourceType - Optional filter to extract only resources of a specific type
 * @returns Array of resources from the Bundle entries
 *
 * @example
 * ```typescript
 * const result = await executeTransaction(medplum, entries);
 *
 * // Get all resources
 * const allResources = extractResourcesFromTransactionResult(result);
 *
 * // Get only Observations
 * const observations = extractResourcesFromTransactionResult(result, 'Observation');
 * ```
 * @agentic @benchmark/core
 */ function extractResourcesFromTransactionResult(bundle, resourceType) {
    const entries = bundle.entry || [];
    return entries.map((entry)=>entry.resource).filter((resource)=>!!resource).filter((resource)=>!resourceType || resource.resourceType === resourceType);
}
/**
 * Extract a single resource from a Bundle transaction result by resource type
 *
 * Useful when you know the transaction should return exactly one resource of a given type.
 * Throws an error if no resource is found or multiple resources of the type exist.
 *
 * @param bundle - Bundle result from executeTransaction
 * @param resourceType - Resource type to extract
 * @returns The single resource of the specified type
 * @throws Error if zero or multiple resources of the type are found
 *
 * @example
 * ```typescript
 * const result = await executeTransaction(medplum, entries);
 * const encounter = extractSingleResourceFromTransactionResult(result, 'Encounter');
 * ```
 * @agentic @benchmark/core
 */ function extractSingleResourceFromTransactionResult(bundle, resourceType) {
    const resources = extractResourcesFromTransactionResult(bundle, resourceType);
    if (resources.length === 0) {
        throw new Error(`No ${resourceType} resource found in transaction result`);
    }
    if (resources.length > 1) {
        throw new Error(`Expected 1 ${resourceType} resource but found ${resources.length}`);
    }
    return resources[0];
}

export { DEFAULT_RATE_LIMIT_POLICY, PATIENT_UNDER_18_FLAG_CODE, RATE_LIMIT_BUFFER_MS, addBusinessDays, buildUsPhoneTelecomSearchValues, calculateAge, contactValidation, dateOnlyToISO, decodeBase64Utf8, decodeMarkdownAttachment, encodeBase64Utf8, encodeMarkdownAttachment, executeTransaction, extractPractitionerPhoneNumber, extractPractitionerRolePhoneNumber, extractPractitionerRolePhoneNumbers, extractResourcesFromTransactionResult, extractSingleResourceFromTransactionResult, findDuplicatePhoneLikeTelecomIndices, formatAddress, formatPhoneNumber, getDisplayValueOrReference, getMarkdownByteLength, getPatientMemberListPhoneTelecomValues, getResourceIdFromReference, hasDisplayValue, isConfirmedAdult, isPatientUnder18ForNps, isUnder18ByBirthDate, isValidFhirDate, localCalendarDateTodayISO, nextBusinessDay, parseFhirInstantForDisplay, parseRateLimitFromBundle, parseRateLimitFromError, parseUSPhoneWithExtension, sleep, toDateInputValue, toE164US, toLogSafeError, toTelURIUS, withBundleRateLimitRetry, withRateLimitRetry };
